import type React from 'react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import {
  type AuthUser,
  EmployeeNotFoundError,
  getCurrentUser,
  logout as logoutApi,
} from '../utils/auth';
import { logger } from '../utils/logger';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const EMPLOYEE_NOT_FOUND_FLAG = 'employee_not_found';

// IMPORTANT: This is a temporary flag to bypass authentication for UI testing.
// It should be set to false in production.
// 🔧 DEV MODE: Set to true to bypass authentication for UI testing
const DEV_BYPASS_AUTH = false;

// Mock user for development
const MOCK_USER: AuthUser = {
  userId: 'dev-user-123',
  userName: 'Test User',
  userEmail: 'test@example.com',
};

export const setEmployeeNotFoundFlag = () => {
  localStorage.setItem(EMPLOYEE_NOT_FOUND_FLAG, 'true');
};

export const clearEmployeeNotFoundFlag = () => {
  localStorage.removeItem(EMPLOYEE_NOT_FOUND_FLAG);
};

export const hasEmployeeNotFoundFlag = (): boolean => {
  return localStorage.getItem(EMPLOYEE_NOT_FOUND_FLAG) === 'true';
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadUser = useCallback(async () => {
    logger.debug('[AuthContext] loadUser called');
    setIsLoading(true);

    // 🔧 DEV MODE: Skip authentication if flag is enabled
    if (DEV_BYPASS_AUTH) {
      logger.debug('[AuthContext] DEV MODE: Using mock user, bypassing authentication');
      setUser(MOCK_USER);
      setIsLoading(false);
      return;
    }

    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      // Clear the flag if user successfully authenticated
      if (currentUser) {
        logger.debug('[AuthContext] User found, clearing employee_not_found flag');
        clearEmployeeNotFoundFlag();
      } else {
        logger.debug('[AuthContext] No user returned, user is null');
      }
    } catch (error) {
      logger.debug('[AuthContext] Error caught:', error);
      logger.debug('[AuthContext] Error type:', error?.constructor?.name);

      if (error instanceof EmployeeNotFoundError) {
        logger.warn(
          '[AuthContext] Employee not found, setting flag and redirecting to contact support'
        );
        setEmployeeNotFoundFlag();
        setUser(null);
        setIsLoading(false);
        window.location.href = '/contact-support';
        return;
      }
      logger.error('[AuthContext] Failed to load user:', error);
      setUser(null);
    } finally {
      logger.debug('[AuthContext] loadUser completed, setting isLoading to false');
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const refreshUser = useCallback(async () => {
    await loadUser();
  }, [loadUser]);

  const logout = useCallback(async () => {
    await logoutApi();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: user !== null,
        refreshUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
